import { Alert, Snackbar } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddButton from "../../../../components/element/AddButton";
import AircraftDetailDialog from "../../../../components/element/AircraftDetailDialog";
import CancelFlightDialog from "../../../../components/element/CancelFlightDialog";
import DeleteSpecialOnboarding from "../../../../components/element/DeleteSpecialOnboarding";
import OvertimeDialog from "../../../../components/element/OvertimeDialog";
import RejectFlightPlanDialog from "../../../../components/element/RejectFlightPlanDialog";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";
import { useFormData } from "../../../../helpers/FormDataProvider";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { useConfiguration } from "../../../../services/ConfigurationProvider";
import { StorageContext } from "../../../../services/StorageProvider";
import {
  getDiplomatsPassenger,
  getGovernmentPassenger,
  getInfantPassenger,
  getMilitaryPassenger,
} from "../../../../utils/configUtils";
import {
  AirportType,
  ClearanceStatus,
  DisputeStatus,
  FLIGHT_PLAN_STATUS,
} from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import {
  getAirportData,
  isDepartureAirportDomestic,
} from "../../../../utils/utils";
import "./EditFlightRoster.css";
import EditFlightRosterTable from "./Tables/EditFlightRosterTable";
import AddPassengerCountDialog from "./Views/AddPassengerCountDialog";
import DisputeReasonDialog from "./Views/DisputeReasonDialog";
import EditFlightPaymentSummary from "./Views/EditFlightPaymentSummary";
import EditFlightRosterApprovedView from "./Views/EditFlightRosterApprovedView";
import EditFlightRosterHeader from "./Views/EditFlightRosterHeader";
import EditFlightSpecialOnboarding from "./Views/EditFlightSpecialOnboarding";
import PassengerCountDisplay from "./Views/PassengerCountDisplay";
import PassengerCountMismatchDialog from "./Views/PassengerCountMismatchDialog";
import RejectBMSConfirmationDialog from "./Views/RejectBMSConfirmationDialog";
import UpdateAirlineCountDialog from "./Views/UpdateAirlineCountDialog";
import VerificationIcon from "../../../../components/element/VerificationIcon";

const EditFlightRoster = () => {
  const { userData } = useFormData();
  const queryClient = useQueryClient();
  const {
    fetchCommercialFlightPlan,
    approveAACommercialFlightPlan,
    approveAirlineCommercialFlightPlan,
    checkAirlineApprovalDispute,
    cancelCommercialFlightPlan,
    editCommercialFlightPlan,
    fetchCommercialFlightPlanApprovals,
    deleteEscortService,
    fetchCommercialEscort,
    updateAirlinePassengerCount,
    checkOvertimeFee,
  } = useAirlineAdminApi(userData.isAAUser);
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const { airportData } = useContext(StorageContext);
  const [flightPlanDetail, setFlightPlanDetail] = useState(null);
  const [editedFlightDetails, setEditedFlightDetails] = useState({});
  const [selectedOrigin, setSelectedOrigin] = useState({});
  const [selectedDestination, setSelectedDestination] = useState({});
  const [showCancelFlightDialog, setShowCancelFlightDialog] = useState(false);
  const [showAircraftDetailDialog, setShowAircraftDetailDialog] =
    useState(false);
  const [showOvertimeDialog, setShowOvertimeDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteEscortServiceDialog, setShowDeleteEscortServiceDialog] =
    useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [airlineName, setAirlineName] = useState("");
  const [showAddPassengerCountDialog, setShowAddPassengerCountDialog] =
    useState(false);
  const [passengerCountData, setPassengerCountData] = useState(null);
  const [
    showPassengerCountMismatchDialog,
    setShowPassengerCountMismatchDialog,
  ] = useState(false);
  const [showRejectBMSConfirmationDialog, setShowRejectBMSConfirmationDialog] =
    useState(false);
  const [showUpdateAirlineCountDialog, setShowUpdateAirlineCountDialog] =
    useState(false);
  const [disputeData, setDisputeData] = useState(null);
  const [showDisputeReasonDialog, setShowDisputeReasonDialog] = useState(false);
  const [showDisputeSuccessMessage, setShowDisputeSuccessMessage] =
    useState(false);
  const { configurationData } = useConfiguration();

  const governmentPassengerEnabled = getGovernmentPassenger(configurationData);
  const diplomatsPassengerEnabled = getDiplomatsPassenger(configurationData);
  const militaryPassengerEnabled = getMilitaryPassenger(configurationData);
  const infantPassengerEnabled = getInfantPassenger(configurationData);

  const { data: flightPlanApprovals, isLoading: isFlightPlanApprovalsLoading } =
    useQuery({
      queryKey: ["get-commercial-flight-plan-approvals", flightPlanDetail?.id],
      queryFn: () => fetchCommercialFlightPlanApprovals(flightPlanDetail?.id),
      enabled:
        !!flightPlanDetail?.id &&
        userData.isAAUser !== null &&
        userData.isAAUser !== undefined,
    });

  const getCommercialFlightPlanMutation = useMutation({
    mutationFn: ({ planId }) => fetchCommercialFlightPlan(planId),
    onSuccess: (response) => {
      localStorage.setItem("flightPlanData", JSON.stringify(response?.data));
      setFlightPlanDetail(response?.data);
      updateFlightPlanData(response?.data);
      if (
        response?.data?.airlineExemptedPassengerCount > 0 ||
        response?.data?.airlinePassengerCount > 0
      ) {
        // Calculate total exempted based on configuration flags
        let calculatedTotalExempted = 0;

        if (infantPassengerEnabled) {
          calculatedTotalExempted += response?.data?.airlineInfant || 0;
        }

        if (militaryPassengerEnabled) {
          calculatedTotalExempted += response?.data?.airlineMilitary || 0;
        }

        if (governmentPassengerEnabled) {
          calculatedTotalExempted += response?.data?.airlineGovernment || 0;
        }

        if (diplomatsPassengerEnabled) {
          calculatedTotalExempted += response?.data?.airlineDiplomats || 0;
        }

        const airlineData = {
          totalBoarded: response?.data?.airlinePassengerCount || 0,
          totalExempted: calculatedTotalExempted,
          infant: response?.data?.airlineInfant || 0,
          military: response?.data?.airlineMilitary || 0,
          government: response?.data?.airlineGovernment || 0,
          diplomat: response?.data?.airlineDiplomats || 0,
        };
        setPassengerCountData(airlineData);
      }
    },
  });

  const editCommercialFlightPlanMutation = useMutation({
    mutationFn: ({ planId, data }) => editCommercialFlightPlan(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanDetail?.id,
      ]);
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanDetail?.id,
      });
      setShowRejectDialog(false);
      setIsEditing(false);
    },
  });

  const cancelFlightPlanMutation = useMutation({
    mutationFn: ({ planId }) => cancelCommercialFlightPlan(planId),
    onSuccess: () => {
      setShowCancelFlightDialog(false);
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER);
    },
  });

  const checkDisputeMutation = useMutation({
    mutationFn: ({ planId, raiseDispute, disputeReason }) =>
      checkAirlineApprovalDispute(planId, raiseDispute, disputeReason),
    onSuccess: (response) => {
      setShowDisputeReasonDialog(false);
      if (response?.data?.isDispute === true) {
        setDisputeData(response?.data);
        // If disputeStatus is "RAISED", it means a reason was submitted, so close the mismatch dialog
        if (response?.data?.disputeStatus === DisputeStatus.RAISED) {
          setShowPassengerCountMismatchDialog(false);
          // Refresh data after dispute with reason is submitted
          getCommercialFlightPlanMutation.mutate({
            planId: flightPlanDetail?.id,
          });
          queryClient.invalidateQueries([
            "get-commercial-flight-plan-approvals",
            flightPlanDetail?.id,
          ]);
        } else {
          // Regular dispute without reason, show mismatch dialog
          setShowPassengerCountMismatchDialog(true);
        }
      } else {
        setShowDisputeSuccessMessage(true);

        getCommercialFlightPlanMutation.mutate({
          planId: flightPlanDetail?.id,
        });
        queryClient.invalidateQueries([
          "get-commercial-flight-plan-approvals",
          flightPlanDetail?.id,
        ]);
      }
    },
  });
  const rejectBMSDisputeMutation = useMutation({
    mutationFn: ({ planId, raiseDispute }) =>
      checkAirlineApprovalDispute(planId, raiseDispute),
    onSuccess: (response) => {
      setShowRejectBMSConfirmationDialog(false);
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanDetail?.id,
      });
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanDetail?.id,
      ]);
    },
  });

  const approveFlightPlanMutation = useMutation({
    mutationFn: ({ planId }) =>
      userData.isAAUser
        ? approveAACommercialFlightPlan(planId)
        : approveAirlineCommercialFlightPlan(planId),

    onSuccess: () => {
      setShowDisputeSuccessMessage(true);
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanDetail?.id,
      });
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanDetail?.id,
      ]);
    },
  });
  const deleteEscortServiceMutation = useMutation({
    mutationFn: ({ planId }) => deleteEscortService(planId),
    onSuccess: () => {
      setShowDeleteEscortServiceDialog(false);
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanDetail?.id,
      });
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanDetail?.id,
      ]);
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-fee",
        flightPlanDetail?.id,
      ]);
    },
  });

  const updatePassengerCountMutation = useMutation({
    mutationFn: ({ planId, disputeData }) =>
      updateAirlinePassengerCount(planId, disputeData),
    onSuccess: (response) => {
      setShowUpdateAirlineCountDialog(false);

      setShowAddPassengerCountDialog(false);
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanDetail?.id,
      });
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanDetail?.id,
      ]);
    },
  });

  const checkOvertimeMutation = useMutation({
    mutationFn: ({ data }) => checkOvertimeFee(data),
    onSuccess: (response) => {
      const updatedFlightDetails = {
        ...editedFlightDetails,
        requiresArrivalOvertime: response?.data?.requiresArrivalOvertime,
        requiresDepartureOvertime: response?.data?.requiresDepartureOvertime,
      };
      setEditedFlightDetails(updatedFlightDetails);

      const departureAirportData = getAirportData(
        airportData,
        flightPlanDetail?.departureAirportId
      );
      const arrivalAirportData = getAirportData(
        airportData,
        flightPlanDetail?.arrivalAirportId
      );

      if (
        (response?.data?.requiresArrivalOvertime &&
          arrivalAirportData?.airportType === AirportType.DOMESTIC) ||
        (response?.data?.requiresDepartureOvertime &&
          departureAirportData?.airportType === AirportType.DOMESTIC)
      ) {
        setShowOvertimeDialog(true);
      } else {
        // No overtime required, proceed with saving
        editCommercialFlightPlanMutation.mutate({
          planId: flightPlanDetail?.id,
          data: updatedFlightDetails,
        });
      }
    },
  });

  useEffect(() => {
    // Only proceed if userData.isAAUser is properly set (not null or undefined)
    if (userData.isAAUser === null || userData.isAAUser === undefined) {
      return;
    }

    // Get stored flight plan data and airline names
    const storedFlightPlan = localStorage.getItem("flightPlanData");

    if (storedFlightPlan) {
      const flightPlan = JSON.parse(storedFlightPlan);
      setFlightPlanDetail(flightPlan);

      // Get commercial flight plan data
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlan.id,
      });

      updateFlightPlanData(flightPlan);
    }
  }, [userData.isAAUser]);

  // Set selectedOrigin and selectedDestination from flightPlanDetail
  useEffect(() => {
    if (flightPlanDetail && airportData) {
      const departureAirport = getAirportData(
        airportData,
        flightPlanDetail?.departureAirportId
      );
      const arrivalAirport = getAirportData(
        airportData,
        flightPlanDetail?.arrivalAirportId
      );

      if (departureAirport) {
        setSelectedOrigin(departureAirport);
      }
      if (arrivalAirport) {
        setSelectedDestination(arrivalAirport);
      }
    }
  }, [flightPlanDetail, airportData]);

  const { data: commercialEscortService } = useQuery({
    queryKey: ["get-commercial-escort", flightPlanDetail?.id],
    queryFn: () => fetchCommercialEscort(flightPlanDetail?.id),
    enabled:
      !!flightPlanDetail?.id &&
      userData.isAAUser !== null &&
      userData.isAAUser !== undefined,
  });
  const escortArrivalStatus =
    commercialEscortService?.data?.escortService?.arrivalStatus ===
      ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.arrivalStatus;
  const escortDepartureStatus =
    commercialEscortService?.data?.escortService?.departureStatus ===
      ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.departureStatus;

  const handleConfirmFlight = () => {
    if (
      !editedFlightDetails?.aircraftTypeId ||
      editedFlightDetails.aircraftTypeId === ""
    ) {
      setAlertMessage("Please select aircraft type.");
      setShowAlert(true);
    } else if (
      !editedFlightDetails?.aircraftRegistrationNumber ||
      editedFlightDetails?.aircraftRegistrationNumber === ""
    ) {
      setAlertMessage("Please enter the tail number.");
      setShowAlert(true);
    } else {
      const isArrivalApproved =
        escortArrivalStatus === ClearanceStatus.PAID ||
        escortArrivalStatus === ClearanceStatus.CLEARANCE_GRANTED ||
        escortArrivalStatus === ClearanceStatus.CLEARANCE_REJECTED ||
        escortArrivalStatus === ClearanceStatus.CORRECTION_DONE;

      const isDepartureApproved =
        escortDepartureStatus === ClearanceStatus.PAID ||
        escortDepartureStatus === ClearanceStatus.CLEARANCE_GRANTED ||
        escortDepartureStatus === ClearanceStatus.CLEARANCE_REJECTED ||
        escortDepartureStatus === ClearanceStatus.CORRECTION_DONE;

      if (flightPlanDetail?.requiresEscortService === false) {
        if (userData.isAAUser) {
          approveFlightPlanMutation.mutate({ planId: flightPlanDetail?.id });
        } else {
          checkDisputeMutation.mutate({
            planId: flightPlanDetail?.id,
            raiseDispute: false,
          });
        }
      } else if (
        escortArrivalStatus === null &&
        escortDepartureStatus === null
      ) {
        if (userData.isAAUser) {
          approveFlightPlanMutation.mutate({ planId: flightPlanDetail?.id });
        } else {
          checkDisputeMutation.mutate({
            planId: flightPlanDetail?.id,
            raiseDispute: false,
          });
        }
      } else if (escortArrivalStatus === null) {
        if (isDepartureApproved) {
          if (userData.isAAUser) {
            approveFlightPlanMutation.mutate({ planId: flightPlanDetail?.id });
          } else {
            checkDisputeMutation.mutate({
              planId: flightPlanDetail?.id,
              raiseDispute: false,
            });
          }
        } else {
          setAlertMessage(
            "Special Onboarding approval must be completed before approving the flight plan."
          );
          setShowAlert(true);
        }
      } else if (escortDepartureStatus === null) {
        if (isArrivalApproved) {
          if (userData.isAAUser) {
            approveFlightPlanMutation.mutate({ planId: flightPlanDetail?.id });
          } else {
            checkDisputeMutation.mutate({
              planId: flightPlanDetail?.id,
              raiseDispute: false,
            });
          }
        } else {
          setAlertMessage(
            "Special Onboarding approval must be completed before approving the flight plan."
          );
          setShowAlert(true);
        }
      } else if (isArrivalApproved && isDepartureApproved) {
        if (userData.isAAUser) {
          approveFlightPlanMutation.mutate({ planId: flightPlanDetail?.id });
        } else {
          checkDisputeMutation.mutate({
            planId: flightPlanDetail?.id,
            raiseDispute: false,
          });
        }
      } else {
        setAlertMessage(
          "Special Onboarding must be approved before confirming the flight plan."
        );
        setShowAlert(true);
      }
    }
  };

  const updateFlightPlanData = (planData) => {
    const storedAirlineNames = localStorage.getItem("airlineNames");

    // Preserve any unsaved changes when updating flight plan data
    setEditedFlightDetails((prevEditedDetails) => ({
      aircraftRegistrationNumber:
        prevEditedDetails?.aircraftRegistrationNumber ||
        planData.aircraftRegistrationNumber,
      aircraftTypeId:
        prevEditedDetails?.aircraftTypeId || planData.aircraftTypeId || "",
      departureDelay:
        prevEditedDetails?.departureDelay ||
        (planData.departureDelay ?? "00:00:00"),
      arrivalDelay:
        prevEditedDetails?.arrivalDelay ||
        (planData.arrivalDelay ?? "00:00:00"),
      departureDelayFromArrivalAirport:
        prevEditedDetails?.departureDelayFromArrivalAirport ||
        (planData.departureDelayFromArrivalAirport ?? "00:00:00"),
      passengerBoardingCount: planData.passengerBoardingCount,
      passengerScreeningCount: planData.scannedPassengerCount,
    }));

    // Try to get airline name from stored names
    if (storedAirlineNames && planData?.airlineId) {
      const airlineNames = JSON.parse(storedAirlineNames);
      const name = airlineNames[planData.airlineId];
      if (name) {
        setAirlineName(name);
      }
    }
  };

  const editFlightPlanWithValidation = () => {
    if (
      !editedFlightDetails?.aircraftTypeId ||
      editedFlightDetails.aircraftTypeId === ""
    ) {
      setAlertMessage("Please select aircraft type.");
      setShowAlert(true);
    } else if (
      !editedFlightDetails?.aircraftRegistrationNumber ||
      editedFlightDetails?.aircraftRegistrationNumber === ""
    ) {
      setAlertMessage("Please enter the tail number.");
      setShowAlert(true);
    } else {
      // Check if terminals were removed (changed from selected to main terminal)
      const departureTerminalRemoved =
        editedFlightDetails?.removeDepartureTerminal === true;
      const arrivalTerminalRemoved =
        editedFlightDetails?.removeArrivalTerminal === true;

      // If any terminal is still selected, skip overtime check and directly save
      if (
        editedFlightDetails?.departureAirportTerminalId ||
        editedFlightDetails?.arrivalAirportTerminalId
      ) {
        editCommercialFlightPlanMutation.mutate({
          planId: flightPlanDetail?.id,
          data: editedFlightDetails,
        });
      } else if (departureTerminalRemoved || arrivalTerminalRemoved) {
        // Terminal was removed (changed from selected to main), check overtime
        const data = {
          arrivalAirportId: flightPlanDetail?.arrivalAirportId,
          departureAirportId: flightPlanDetail?.departureAirportId,
          arrivalTime: flightPlanDetail?.arrivalTime,
          departureTime: flightPlanDetail?.departureTime,
        };
        checkOvertimeMutation.mutate({ data });
      } else {
        // No terminal changes, directly save
        editCommercialFlightPlanMutation.mutate({
          planId: flightPlanDetail?.id,
          data: editedFlightDetails,
        });
      }
    }
  };

  const handleSaveFlight = () => {
    editFlightPlanWithValidation();
  };

  const handleUpdateReasonSave = () => {
    editFlightPlanWithValidation();
  };

  const handleCancelFlight = () => {
    cancelFlightPlanMutation.mutate({
      planId: flightPlanDetail?.id,
    });
  };

  const openDeleteEscortServiceDialog = () => {
    setShowDeleteEscortServiceDialog(true);
  };
  const handleDeleteEscortService = () => {
    deleteEscortServiceMutation.mutate({
      planId: flightPlanDetail?.id,
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAircraftDetailSave = () => { };

  const openSpecialOnboarding = (type) => {
    navigate(
      ROUTE_PATHS.COMMERCIAL_AIRLINE_SPECIAL_ONBOARDING.replace(":type", type)
    );
  };

  const handleAcceptButton = () => {
    setShowOvertimeDialog(false);
    editCommercialFlightPlanMutation.mutate({
      planId: flightPlanDetail?.id,
      data: editedFlightDetails,
    });
  };

  const checkSpecialOnboardingAvailability = () => {
    const departureAirportData = getAirportData(
      airportData,
      flightPlanDetail?.departureAirportId
    );
    const arrivalAirportData = getAirportData(
      airportData,
      flightPlanDetail?.arrivalAirportId
    );

    // Check if each airport with escort fee enabled is available (no terminal selected)
    const isDepartureEscortAvailable =
      departureAirportData?.escortFeeEnabled &&
      !flightPlanDetail?.departureAirportTerminalId;
    const isArrivalEscortAvailable =
      arrivalAirportData?.escortFeeEnabled &&
      !flightPlanDetail?.arrivalAirportTerminalId;

    // Show card if at least one airport with escort fee enabled is available
    const canShowCard = isDepartureEscortAvailable || isArrivalEscortAvailable;

    return canShowCard;
  };

  // Function to handle alert close
  const handleAlertClose = () => {
    setShowAlert(false);
  };
  const handleAddPassengerCountSave = (data) => {
    const addPassengerCountData = {
      airlinePassengerCount: data?.totalBoarded ?? 0,
      airlineInfant: data?.infant ?? 0,
      airlineMilitary: data?.military ?? 0,
      airlineGovernment: data?.government ?? 0,
      airlineDiplomats: data?.diplomat ?? 0,
    };

    // Always include airlineDeadheadCrewIds, even if empty
    if (
      data?.airlineDeadheadCrewIds &&
      Array.isArray(data.airlineDeadheadCrewIds)
    ) {
      addPassengerCountData.airlineDeadheadCrewIds =
        data.airlineDeadheadCrewIds;
    } else {
      addPassengerCountData.airlineDeadheadCrewIds = [];
    }

    // Call dispute API
    updatePassengerCountMutation.mutate({
      planId: flightPlanDetail?.id,
      disputeData: addPassengerCountData,
    });
    setPassengerCountData(data);
  };

  const handleRejectBMSCount = () => {
    // Show the dispute reason dialog
    setShowPassengerCountMismatchDialog(false);
    setShowDisputeReasonDialog(true);
  };

  const handleUpdateAirlineCount = () => {
    // Show the update airline count dialog
    setShowPassengerCountMismatchDialog(false);
    setShowUpdateAirlineCountDialog(true);
  };

  const handleRejectBMSSave = () => {
    // Handle saving the rejection - mark flight as disputed
    rejectBMSDisputeMutation.mutate({
      planId: flightPlanDetail?.id,
      raiseDispute: true,
    });
  };

  const handleRejectBMSCancel = () => {
    // Cancel the rejection and go back to mismatch dialog
    setShowRejectBMSConfirmationDialog(false);
    setShowPassengerCountMismatchDialog(true);
  };

  // Function to handle dispute reason submission
  const handleDisputeReasonSave = (disputeReason) => {
    const mutationData = {
      planId: flightPlanDetail?.id,
      raiseDispute: true,
    };

    // Only add disputeReason if it's not empty
    if (disputeReason && disputeReason.trim()) {
      mutationData.disputeReason = disputeReason.trim();
    }
    setShowPassengerCountMismatchDialog(false);

    checkDisputeMutation.mutate(mutationData);
  };

  const handleUpdateAirlineCountSave = (data) => {
    // Handle saving the updated airline count
    const updatedPassengerCountData = {
      airlinePassengerCount: data?.totalBoarded,
      airlineInfant: data?.infant,
      airlineMilitary: data?.military,
      airlineGovernment: data?.government,
      airlineDiplomats: data?.diplomat,
    };
    updatePassengerCountMutation.mutate({
      planId: flightPlanDetail?.id,
      disputeData: updatedPassengerCountData,
    });
    setEditedFlightDetails((prev) => ({
      ...prev,
      passengerBoardingCount: data.totalBoarded,
    }));
    setPassengerCountData((prev) => ({
      ...prev,
      totalBoarded: data.totalBoarded,
      totalExempted: data.totalExempted,
      infant: data.infant,
      military: data.military,
      government: data.government,
      diplomat: data.diplomat,
    }));
  };

  const getPassengerMismatchData = () => {
    const bmsCount = disputeData?.scannedPassengerCount || 0;

    const exempted = disputeData?.exemptedPassengerCount || 0;
    const billableCount = bmsCount - exempted;

    return {
      bmsCount,
      exempted,
      billableCount,
    };
  };
  const getAirlinePassengerData = () => {
    const airlineCount = disputeData?.airlinePassengerCount || 0;

    const exempted = disputeData?.airlineExemptedPassengerCount || 0;
    const billableCount = airlineCount - exempted;

    return {
      airlineCount,
      exempted,
      billableCount,
    };
  };

  const getAirlineCountClassName = () => {
    if (flightPlanDetail?.disputeStatus === DisputeStatus.AA_FORCED_BMS_COUNT) {
      return "passenger-count-rejected";
    }
    if (
      flightPlanDetail?.disputeStatus ===
      DisputeStatus.AA_ACCEPTED_AIRLINE_COUNT &&
      userData.isAAUser
    ) {
      return "passenger-count-accepted";
    }
    if (flightPlanDetail?.disputeStatus === DisputeStatus.RAISED) {
      return "passenger-count-rejected";
    }
    return "";
  };
  const departureAirportType = getAirportData(
    airportData,
    flightPlanDetail?.departureAirportId
  )?.airportType;
  const isDepartureAirportInternational =
    departureAirportType === AirportType.INTERNATIONAL;

  return (
    <div className="edit-flight-roster-container">
      {updatePassengerCountMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          {updatePassengerCountMutation.data?.data || "Success"}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {editCommercialFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {editCommercialFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {approveFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {approveFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {cancelFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {cancelFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {updatePassengerCountMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {updatePassengerCountMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {showDisputeSuccessMessage ? (
        <SnackbarAlert type={"success"}>
          Flight plan approved successfully
        </SnackbarAlert>
      ) : (
        ""
      )}
      {checkDisputeMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {checkDisputeMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {checkOvertimeMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {checkOvertimeMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {showAlert && (
        <Snackbar
          open={showAlert}
          autoHideDuration={2000}
          onClose={handleAlertClose}
        >
          <Alert
            onClose={handleAlertClose}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      )}
      <EditFlightRosterHeader
        isEditing={isEditing}
        isAAUser={userData.isAAUser}
        flightPlanDetail={flightPlanDetail}
        flightPlanApprovals={flightPlanApprovals?.data}
        isFlightPlanApprovalsLoading={isFlightPlanApprovalsLoading}
        onClickAircraftDetail={() => setShowAircraftDetailDialog(true)}
        onClickCancelFlight={() => setShowCancelFlightDialog(true)}
        onClickEdit={() => setIsEditing(true)}
        onClickConfirm={handleConfirmFlight}
        onClickCancel={() => setIsEditing(false)}
        onClickSave={handleSaveFlight}
        isLoadingConfirm={
          checkDisputeMutation?.isPending ||
          approveFlightPlanMutation?.isPending
        }
        isLoadingSave={editCommercialFlightPlanMutation?.isPending}
        onClickPrint={handlePrint}
        airlineName={airlineName}
      />
      {userData?.isAAUser &&
        (flightPlanDetail?.adsbVerified || flightPlanDetail?.bmsVerified) && (
          <div className="edit-flight-roster-container-verification-icon-divider-row">
            <VerificationIcon
              adsbVerified={flightPlanDetail?.adsbVerified}
              bmsVerified={flightPlanDetail?.bmsVerified}
            />
          </div>
        )}
      {/* Dispute Status Message */}
      {flightPlanDetail?.disputeStatus === DisputeStatus.RAISED ? (
        <div className="edit-flight-passenger-count-container">
          <div className="dispute-status-text">
            Since you have not accepted the BMS Passenger Count, this flight
            record is sent for dispute processing to Airport Authority.
          </div>
        </div>
      ) : null}
      <EditFlightRosterTable
        isEditing={isEditing}
        airportData={airportData}
        flightPlanDetail={flightPlanDetail}
        editedFlightDetails={editedFlightDetails}
        setEditedFlightDetails={setEditedFlightDetails}
      />
      {(userData.isAAUser ||
        flightPlanDetail?.disputeStatus ===
        DisputeStatus.AA_FORCED_BMS_COUNT) && (
          <PassengerCountDisplay flightPlanDetail={flightPlanDetail} />
        )}
      {isDepartureAirportDomestic(
        airportData,
        flightPlanDetail?.departureAirportId,
        AirportType
      ) &&
        !getCommercialFlightPlanMutation?.isPending && (
          <div className="edit-flight-passenger-count-container">

            <>
              <div className="passenger-count-display-container">
                <div className="passenger-count-table">
                  <div className="passenger-count-table-header">
                    <div className="passenger-count-table-cell">
                      Airline Total Boarded Pax
                    </div>
                    <div className="passenger-count-table-cell">
                      Airline Exempted Pax
                    </div>
                    {infantPassengerEnabled && (
                      <div className="passenger-count-table-cell">Infant</div>
                    )}
                    {militaryPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        Military
                      </div>
                    )}
                    {governmentPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        Government
                      </div>
                    )}
                    {diplomatsPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        Diplomat
                      </div>
                    )}
                    <div className="passenger-count-table-cell">
                      Crew Members
                    </div>
                  </div>
                  <div className="passenger-count-table-row">
                    <div className="passenger-count-table-cell">
                      <p className={getAirlineCountClassName()}>
                        {passengerCountData?.totalBoarded ?? 0}
                      </p>
                    </div>
                    <div className="passenger-count-table-cell">
                      <p className={getAirlineCountClassName()}>
                        {passengerCountData?.totalExempted ?? 0}
                      </p>
                    </div>
                    {infantPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        <p className={getAirlineCountClassName()}>
                          {passengerCountData?.infant ?? 0}
                        </p>
                      </div>
                    )}
                    {militaryPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        <p className={getAirlineCountClassName()}>
                          {passengerCountData?.military ?? 0}
                        </p>
                      </div>
                    )}
                    {governmentPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        <p className={getAirlineCountClassName()}>
                          {passengerCountData?.government ?? 0}
                        </p>
                      </div>
                    )}
                    {diplomatsPassengerEnabled && (
                      <div className="passenger-count-table-cell">
                        <p className={getAirlineCountClassName()}>
                          {passengerCountData?.diplomat ?? 0}
                        </p>
                      </div>
                    )}
                    <div className="passenger-count-table-cell">
                      {flightPlanDetail?.deadheadCrews &&
                        flightPlanDetail.deadheadCrews.length > 0 ? (
                        <div className="view-history-crew-members-list">
                          {flightPlanDetail.deadheadCrews.map(
                            (crew, index) => (
                              <div
                                key={index}
                                className="view-history-crew-member-item"
                              >
                                <div className="view-history-crew-member-name">
                                  {crew.passengerName} - {crew.seatNumber}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className={getAirlineCountClassName()}>0</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="passenger-count-divider"></div>
                {!userData.isAAUser &&
                  !isDepartureAirportInternational &&
                  flightPlanDetail?.isAutoCompleted !== undefined &&
                  flightPlanDetail?.isAutoCompleted === false &&
                  flightPlanDetail?.status !== FLIGHT_PLAN_STATUS.CANCELLED &&
                  ((flightPlanDetail?.disputeStatus ===
                    DisputeStatus.NOT_RAISED &&
                    flightPlanDetail?.status !==
                    FLIGHT_PLAN_STATUS.AA_APPROVAL &&
                    flightPlanDetail?.status !==
                    FLIGHT_PLAN_STATUS.AIRLINE_APPROVAL) ||
                    flightPlanDetail?.disputeStatus === undefined) && (
                    <button
                      className="passenger-count-update-button"
                      onClick={() => setShowAddPassengerCountDialog(true)}
                    >
                      Update
                    </button>
                  )}
              </div>
            </>

          </div>
        )}
      {(flightPlanApprovals?.data?.length >= 1 ||
        flightPlanDetail?.isAutoCompleted) && (
          <EditFlightRosterApprovedView
            flightApprovalData={flightPlanApprovals?.data}
            disputeStatus={flightPlanDetail?.disputeStatus}
            flightPlanDetail={flightPlanDetail}
          />
        )}
      <div className="edit-flight-details-container">
        {flightPlanDetail?.status !== FLIGHT_PLAN_STATUS.CANCELLED ? (
          <EditFlightPaymentSummary
            flightPlanDetail={flightPlanDetail}
            requiresEscortService={flightPlanDetail?.requiresEscortService}
            isArrivalApproved={
              commercialEscortService?.data?.escortService?.arrivalStatus ===
              ClearanceStatus.NOT_REQUIRED ||
              escortArrivalStatus === ClearanceStatus.CLEARANCE_REJECTED ||
              escortArrivalStatus === ClearanceStatus.CANCELLED
            }
            isDepartureApproved={
              commercialEscortService?.data?.escortService?.departureStatus ===
              ClearanceStatus.NOT_REQUIRED ||
              escortDepartureStatus === ClearanceStatus.CLEARANCE_REJECTED ||
              escortDepartureStatus === ClearanceStatus.CANCELLED
            }
          />
        ) : (
          <div />
        )}
        {flightPlanDetail?.status === ClearanceStatus.CANCELLED &&
          !flightPlanDetail?.requiresEscortService ? (
          <></>
        ) : (
          !userData.isAAUser &&
          checkSpecialOnboardingAvailability() && (
            <EditFlightSpecialOnboarding
              addSpecialOnboarding={openSpecialOnboarding}
              isAddedSpecialOnboarding={flightPlanDetail?.requiresEscortService}
              deleteEscortService={openDeleteEscortServiceDialog}
              tripStatus={flightPlanDetail?.status}
              arrivalEscortStatus={
                commercialEscortService?.data?.escortService?.arrivalStatus
              }
              departureEscortStatus={
                commercialEscortService?.data?.escortService?.departureStatus
              }
              airportData={airportData}
              arrivalAirport={flightPlanDetail?.arrivalAirportId}
              departureAirport={flightPlanDetail?.departureAirportId}
              arrivalRejectionReason={
                commercialEscortService?.data?.escortService
                  ?.arrivalRejectionReason
              }
              departureRejectionReason={
                commercialEscortService?.data?.escortService
                  ?.departureRejectionReason
              }
              hasDepartureTerminal={
                !!flightPlanDetail?.departureAirportTerminalId
              }
              hasArrivalTerminal={!!flightPlanDetail?.arrivalAirportTerminalId}
            />
          )
        )}
      </div>

      <CancelFlightDialog
        open={showCancelFlightDialog}
        onClose={() => setShowCancelFlightDialog(false)}
        onClickYes={handleCancelFlight}
        isLoading={cancelFlightPlanMutation?.isPending}
      />
      <AircraftDetailDialog
        open={showAircraftDetailDialog}
        onClose={() => setShowAircraftDetailDialog(false)}
        onClickSave={handleAircraftDetailSave}
        isLoading={false}
        editedFlightDetails={editedFlightDetails}
        setEditedFlightDetails={setEditedFlightDetails}
      />
      <OvertimeDialog
        flightDetails={flightPlanDetail}
        editedFlightDetails={editedFlightDetails}
        isEditScreen={true}
        origin={selectedOrigin}
        destination={selectedDestination}
        open={showOvertimeDialog}
        onClose={() => setShowOvertimeDialog(false)}
        onAccept={handleAcceptButton}
      />
      <RejectFlightPlanDialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onSave={handleUpdateReasonSave}
        isAAUser={userData.isAAUser}
        isLoading={editCommercialFlightPlanMutation?.isPending}
        flightPlanDetail={flightPlanDetail}
        editedFlightDetails={editedFlightDetails}
        setEditedFlightDetails={setEditedFlightDetails}
      />
      <DeleteSpecialOnboarding
        open={showDeleteEscortServiceDialog}
        onClose={() => setShowDeleteEscortServiceDialog(false)}
        onClickYes={handleDeleteEscortService}
        isLoading={deleteEscortServiceMutation?.isPending}
      />

      <AddPassengerCountDialog
        isOpen={showAddPassengerCountDialog}
        onClose={() => setShowAddPassengerCountDialog(false)}
        onSave={handleAddPassengerCountSave}
        initialData={flightPlanDetail}
        isLoading={updatePassengerCountMutation?.isPending}
      />

      <PassengerCountMismatchDialog
        isOpen={showPassengerCountMismatchDialog}
        onClose={() => setShowPassengerCountMismatchDialog(false)}
        onRejectBMSCount={handleRejectBMSCount}
        onUpdateAirlineCount={handleUpdateAirlineCount}
        passengerData={getPassengerMismatchData()}
      />

      <RejectBMSConfirmationDialog
        isOpen={showRejectBMSConfirmationDialog}
        onClose={() => setShowRejectBMSConfirmationDialog(false)}
        onSave={handleRejectBMSSave}
        onCancel={handleRejectBMSCancel}
        passengerData={getAirlinePassengerData()}
        isLoading={rejectBMSDisputeMutation?.isPending}
      />

      <UpdateAirlineCountDialog
        isOpen={showUpdateAirlineCountDialog}
        onClose={() => setShowUpdateAirlineCountDialog(false)}
        onSave={handleUpdateAirlineCountSave}
        initialData={flightPlanDetail}
        isLoading={updatePassengerCountMutation?.isPending}
      />

      <DisputeReasonDialog
        isOpen={showDisputeReasonDialog}
        onClose={() => setShowDisputeReasonDialog(false)}
        onSave={handleDisputeReasonSave}
        isLoading={checkDisputeMutation?.isPending}
      />
    </div>
  );
};

export default EditFlightRoster;
